import React, { useState, useEffect } from 'react';
import { Form, Col, Modal, Button, Row, Spinner } from 'react-bootstrap';
import Select from "react-dropdown-select";
import styled from "styled-components";
import dayjs from 'dayjs';
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import constants from '../../const.js';
import axios from 'axios';
import GarmentManager from '../garment/garment-manager';
import EditSceneInfo from '../scenes/edit-scene-info';

const StyledModal = styled(Modal)`
  .modal-dialog {
    max-width: 900px;
    width: 95vw;
  }

  .modal-content {
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    border: none;
    border-radius: 12px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    overflow: hidden;
  }

  .modal-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    padding: 12px 20px;
  }

  .modal-header .modal-title {
    font-weight: 700;
    font-size: 18px;
    color: white;
  }

  .modal-header .btn-close {
    filter: brightness(0) invert(1);
  }

  .modal-body {
    background: #f8f9fb;
    padding: 16px 20px;
    max-height: 85vh;
    overflow-y: auto;
  }
`;

const FormRow = styled(Row)`
  margin-bottom: 8px;

  > div {
    display: flex;
    flex-direction: column;
  }
`;

const ModernFormGroup = styled(Form.Group)`
  margin-bottom: 8px;

  .form-label {
    font-weight: 600;
    color: #333;
    margin-bottom: 2px;
    display: block;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .form-control,
  .custom-select {
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    padding: 4px 8px;
    font-size: 12px;
    transition: all 0.3s ease;
    background-color: #ffffff;

    &:focus {
      border-color: #667eea;
      box-shadow: 0 0 0 0.1rem rgba(102, 126, 234, 0.15);
    }
  }

  textarea.form-control {
    resize: vertical;
    min-height: 80px;
  }
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #333;
  margin: 12px 0 8px 0;
  padding-bottom: 6px;
  border-bottom: 2px solid #667eea;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ModernButton = styled(Button)`
  border-radius: 6px;
  font-weight: 600;
  padding: 6px 12px;
  transition: all 0.3s ease;
  border: none;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &.btn-primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
    }
  }

  &.btn-secondary {
    background: linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%);
    color: white;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 20px rgba(149, 165, 166, 0.3);
    }
  }

  &.btn-light {
    background: #ffffff;
    color: #667eea;
    border: 2px solid #667eea;

    &:hover {
      background: #667eea;
      color: white;
      transform: translateY(-2px);
    }
  }
`;

const TagBadge = styled.div`
  display: inline-block;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  transition: all 0.3s ease;
  background: linear-gradient(135deg, #fff4e6 0%, #ffe0b3 100%);
  color: #333;
  border: 1px solid #ffc107;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 193, 7, 0.3);
  }
`;

const CategorySection = styled.div`
  margin-bottom: 16px;

  .category-header {
    font-size: 12px;
    font-weight: 700;
    color: #667eea;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 8px;
    display: block;
  }

  .tags-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
`;

const ActorSearchContainer = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 12px;
  align-items: center;

  .search-input-wrapper {
    flex: 1;
    position: relative;

    .search-input {
      width: 100%;
      border: 1px solid #e0e0e0;
      border-radius: 6px;
      padding: 6px 10px;
      font-size: 12px;
      background-color: #ffffff;

      &:focus {
        outline: none;
        border-color: #667eea;
        box-shadow: 0 0 0 0.1rem rgba(102, 126, 234, 0.15);
      }
    }
  }

  .button-group {
    display: flex;
    gap: 4px;
  }
`;

const ActorBadge = styled.div`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.3s ease;
  background: linear-gradient(135deg, #f0f4ff 0%, #e3e9ff 100%);
  color: #333;
  border: 1px solid #667eea;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }
`;

const ActorCard = styled.div`
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  text-align: center;
  padding: 6px;
  position: relative;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .actor-image {
    width: 100%;
    height: auto;
    aspect-ratio: 386 / 520;
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
    border-radius: 6px;
    position: relative;
    overflow: hidden;
    background-color: #f0f0f0;
  }

  .actor-name {
    font-size: 11px;
    font-weight: 600;
    color: #333;
    margin-top: 6px;
    word-break: break-word;
  }

  .delete-badge {
    position: absolute;
    top: 4px;
    right: 4px;
    background: rgba(255, 107, 107, 0.9);
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 107, 107, 1);
      transform: scale(1.1);
    }

    svg {
      color: white;
      font-size: 14px;
    }
  }
`;

const GarmentCard = styled.div`
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  padding: 8px;
  position: relative;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .garment-image {
    width: 100%;
    height: 120px;
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
    border-radius: 6px;
    background-color: #f0f0f0;
  }

  .garment-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    margin-top: 6px;
    min-height: 0;
  }

  .garment-name {
    font-size: 11px;
    font-weight: 600;
    color: #333;
    word-break: break-word;
    margin-bottom: 2px;
  }

  .garment-category {
    font-size: 10px;
    color: #999;
    margin-bottom: 4px;
  }

  .garment-actor {
    font-size: 10px;
    font-weight: 500;
    color: #667eea;
  }

  .delete-badge {
    position: absolute;
    top: 4px;
    right: 4px;
    background: rgba(255, 107, 107, 0.9);
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    z-index: 10;

    &:hover {
      background: rgba(255, 107, 107, 1);
      transform: scale(1.1);
    }

    svg {
      color: white;
      font-size: 14px;
    }
  }
`;

function EditAlbum(props) {
  const { albumId, galleryId, onSave } = props;
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validated, setValidated] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  
  const [album, setAlbum] = useState({
    id: null,
    title: "",
    description: "",
    date: "2023-01-01",
    rating: 0,
    folder_name: ""
  });

  const [actors, setActors] = useState({ original: [], selectedActor: [] });
  const [allActors, setAllActors] = useState([]);
  const [editActor, setEditActor] = useState({
    editActor: false,
    selectedActor: []
  });

  const [tags, setTags] = useState({ original: [], selectedTag: [] });
  const [allTags, setAllTags] = useState([]);
  const [editTag, setEditTag] = useState({
    editTag: false,
    selectedTag: []
  });

  const [linkedScene, setLinkedScene] = useState(null);
  const [galleryFolderName, setGalleryFolderName] = useState(null);
  const [showEditScene, setShowEditScene] = useState(false);
  
  const [albumGarments, setAlbumGarments] = useState([]);
  const [loadingGarments, setLoadingGarments] = useState(false);

  const token = `?token=${localStorage.getItem('token').slice(7)}`;
  const API_URL = constants.DATA1_API_URL || 'http://localhost:5050';

  // Fetch album data
  const fetchAlbumData = async () => {
    try {
      setLoading(true);
      setIsNew(false);
      const response = await axios.get(
        `${API_URL}/gallery/${galleryId}/album/${albumId}`,
        {
          headers: {
            'Authorization': localStorage.getItem('token')
          }
        }
      );
      
      if (response.data) {
        const albumData = response.data;
        setAlbum({
          id: albumData.album_id,
          title: albumData.title || "",
          description: albumData.description || "",
          date: albumData.date ? dayjs(albumData.date).format('YYYY-MM-DD') : "2023-01-01",
          rating: albumData.rating || 0,
          folder_name: albumData.folder_name || ""
        });

        // Fetch actors for this album
        if (albumData.actors && Array.isArray(albumData.actors)) {
          setActors({
            original: albumData.actors,
            selectedActor: albumData.actors
          });
        }

        // Fetch tags for this album
        if (albumData.tags && Array.isArray(albumData.tags)) {
          setTags({
            original: albumData.tags,
            selectedTag: albumData.tags
          });
        }
      }
    } catch (error) {
      // If album not found (404) or any other fetch error, initialize as new album
      setIsNew(true);
      setAlbum({
        id: null,
        title: albumId || "",
        description: "",
        date: dayjs().format('YYYY-MM-DD'),
        rating: 0,
        folder_name: albumId || ""
      });
      setActors({ original: [], selectedActor: [] });
      setTags({ original: [], selectedTag: [] });
      console.error('Error fetching album data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch available actors
  const fetchActors = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/actors`,
        {
          headers: {
            'Authorization': localStorage.getItem('token')
          }
        }
      );
      
      if (response.data && Array.isArray(response.data)) {
        setAllActors(response.data);
      }
    } catch (error) {
      console.error('Error fetching actors:', error);
    }
  };

  // Fetch available tags
  const fetchTags = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/scenes/tags`,
        {
          headers: {
            'Authorization': localStorage.getItem('token')
          }
        }
      );
      
      if (response.data && Array.isArray(response.data)) {
        setAllTags(response.data);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  // Fetch gallery info
  const fetchGalleryInfo = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/gallery/${galleryId}`,
        {
          headers: {
            'Authorization': localStorage.getItem('token')
          }
        }
      );
      if (response.data && response.data.folder_name) {
        setGalleryFolderName(response.data.folder_name);
      }
    } catch (error) {
      console.error('Error fetching gallery info:', error);
    }
  };

  // Fetch linked scene by gallery path
  const fetchLinkedScene = async (galleryName, albumName) => {
    if (!galleryName || !albumName) return;
    try {
      const response = await axios.get(
        `${API_URL}/scenes/gallery/${encodeURIComponent(galleryName)}/${encodeURIComponent(albumName)}`,
        {
          headers: {
            'Authorization': localStorage.getItem('token')
          }
        }
      );
      if (response.data) {
        setLinkedScene(response.data);
      } else {
        setLinkedScene(null);
      }
    } catch (error) {
      // 404 is expected when scene is not found
      if (error.response?.status === 404) {
        setLinkedScene(null);
      } else {
        console.error('Error fetching linked scene:', error);
      }
    }
  };

  // Fetch garments for the album
  const fetchAlbumGarments = async (albumIdParam) => {
    if (!albumIdParam) return;
    try {
      setLoadingGarments(true);
      const response = await axios.get(
        `${API_URL}/garments/albums/${albumIdParam}`,
        {
          headers: {
            'Authorization': localStorage.getItem('token')
          }
        }
      );
      
      if (response.data && response.data.garments) {
        setAlbumGarments(response.data.garments);
      } else {
        setAlbumGarments([]);
      }
    } catch (error) {
      console.error('Error fetching album garments:', error);
      setAlbumGarments([]);
    } finally {
      setLoadingGarments(false);
    }
  };

  // Remove garment from album
  const removeGarmentFromAlbum = async (garmentId) => {
    if (!album.id) return;
    try {
      await axios.delete(
        `${API_URL}/garments/albums/${album.id}/garments/${garmentId}`,
        {
          headers: {
            'Authorization': localStorage.getItem('token')
          }
        }
      );
      
      // Remove from local state
      setAlbumGarments(albumGarments.filter(g => g.id !== garmentId));
    } catch (error) {
      console.error('Error removing garment from album:', error);
      alert('Failed to remove garment from album');
    }
  };

  const handleShow = async () => {
    setShow(true);
    await fetchAlbumData();
    await fetchActors();
    await fetchTags();
    await fetchGalleryInfo();
  };

  const handleClose = () => {
    setShow(false);
    setValidated(false);
    setEditActor({ editActor: false, selectedActor: [] });
    setEditTag({ editTag: false, selectedTag: [] });
  };

  const handleSubmit = async (event) => {
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
    }
    setValidated(true);

    try {
      setSaving(true);

      // Create or update album metadata
      if (isNew) {
        // Create new album
        await axios.post(
          `${API_URL}/gallery/${galleryId}/album`,
          {
            folder_name: albumId,
            title: album.title,
            description: album.description,
            date: album.date,
            rating: parseInt(album.rating)
          },
          {
            headers: {
              'Authorization': localStorage.getItem('token')
            }
          }
        );
        setIsNew(false);
      } else {
        // Update existing album
        await axios.put(
          `${API_URL}/gallery/${galleryId}/album/${albumId}`,
          {
            title: album.title,
            description: album.description,
            date: album.date,
            rating: parseInt(album.rating)
          },
          {
            headers: {
              'Authorization': localStorage.getItem('token')
            }
          }
        );
      }

      // Update actors if changed
      if (JSON.stringify(actors.original) !== JSON.stringify(actors.selectedActor)) {
        // Remove all current actors
        for (const actor of actors.original) {
          try {
            await axios.delete(
              `${API_URL}/gallery/${galleryId}/album/${albumId}/actor/${actor.id}`,
              {
                headers: {
                  'Authorization': localStorage.getItem('token')
                }
              }
            );
          } catch (error) {
            console.error(`Error removing actor ${actor.id}:`, error);
          }
        }

        // Add new actors
        for (const actor of actors.selectedActor) {
          try {
            await axios.post(
              `${API_URL}/gallery/${galleryId}/album/${albumId}/actor/${actor.id}`,
              {},
              {
                headers: {
                  'Authorization': localStorage.getItem('token')
                }
              }
            );
          } catch (error) {
            console.error(`Error adding actor ${actor.id}:`, error);
          }
        }
      }

      // Update tags if changed
      if (JSON.stringify(tags.original) !== JSON.stringify(tags.selectedTag)) {
        // Remove all current tags
        for (const tag of tags.original) {
          try {
            await axios.delete(
              `${API_URL}/gallery/${galleryId}/album/${albumId}/tag/${tag.id}`,
              {
                headers: {
                  'Authorization': localStorage.getItem('token')
                }
              }
            );
          } catch (error) {
            console.error(`Error removing tag ${tag.id}:`, error);
          }
        }

        // Add new tags
        for (const tag of tags.selectedTag) {
          try {
            await axios.post(
              `${API_URL}/gallery/${galleryId}/album/${albumId}/tag/${tag.id}`,
              {},
              {
                headers: {
                  'Authorization': localStorage.getItem('token')
                }
              }
            );
          } catch (error) {
            console.error(`Error adding tag ${tag.id}:`, error);
          }
        }
      }
    
      handleClose();
      if (onSave) {
        onSave();
      }
    } catch (error) {
      console.error('Error saving album:', error);
     
    } finally {
      setSaving(false);
    }
  };

  const addActor = () => {
    const temp = editActor.selectedActor;
    setActors({
      ...actors,
      selectedActor: [...new Set([...actors.selectedActor, ...temp])]
    });
    setEditActor({ editActor: false, selectedActor: [] });
  };

  const removeActor = (actorId) => {
    setActors({
      ...actors,
      selectedActor: actors.selectedActor.filter(actor => actor.id !== actorId)
    });
  };

  const addTag = () => {
    const temp = editTag.selectedTag;
    setTags({
      ...tags,
      selectedTag: [...new Set([...tags.selectedTag, ...temp])]
    });
    setEditTag({ editTag: false, selectedTag: [] });
  };

  const removeTag = (tagId) => {
    setTags({
      ...tags,
      selectedTag: tags.selectedTag.filter(tag => tag.id !== tagId)
    });
  };

  // Get actor image URL
  const getActorImageUrl = (actorId) => {
    const CDN_URL = constants.CONTENT_DOMAIN || 'https://cdn.66movies.com';
    const token = localStorage.getItem('token');
    const tokenParam = token ? `?token=${token.slice(7)}` : '';
    const paddedActorId = String(actorId).padStart(4, '0');
    return `${CDN_URL}download/sus-media/actors/${paddedActorId}/model-large.webp${tokenParam}`;
  };

  // Get garment image URL
  const getGarmentImageUrl = (imageUrl) => {
    if (!imageUrl) return '';
    const CDN_URL = constants.CONTENT_DOMAIN || 'https://cdn.66movies.com';
    const token = localStorage.getItem('token');
    const tokenParam = token ? `?token=${token.slice(7)}` : '';
    // Ensure proper path construction: CDN_URL/garments/{filename}
    const cdnBase = CDN_URL.endsWith('/') ? CDN_URL : CDN_URL + '/';
    const imagePath = imageUrl.startsWith('/') ? imageUrl.slice(1) : imageUrl;
    return `${cdnBase}${imagePath.replace(".webp","_flatlay.webp")}${tokenParam}`;
  };

  // Update linked scene when album or gallery data changes
  useEffect(() => {
    if (galleryFolderName && album.folder_name) {
      fetchLinkedScene(galleryFolderName, album.folder_name);
    }
  }, [galleryFolderName, album.folder_name]);

  // Check if user is admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setIsAdmin(false);
          return;
        }

        const response = await axios.get(
          `${constants.USER_API_URL}/auth/authenticated`,
          {
            headers: {
              'Authorization': token
            }
          }
        );

        if (response.data && response.data.perm) {
          const isUserAdmin = response.data.perm.some(p => p.role && p.role.id <= 2);
          setIsAdmin(isUserAdmin);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, []);

  // Fetch album garments when modal is shown or album ID changes
  useEffect(() => {
    if (show && album.id) {
      fetchAlbumGarments(album.id);
    }
  }, [show, album.id]);

  // Group tags by category
  const getTagsByCategory = () => {
    const grouped = {};
    tags.selectedTag.forEach(tag => {
      const category = tag.category || 'Other';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(tag);
    });
    // Sort categories alphabetically
    return Object.keys(grouped).sort().reduce((result, key) => {
      result[key] = grouped[key];
      return result;
    }, {});
  };

  return (
    <>
      {isAdmin && (
        <ModernButton variant="light" size="sm" onClick={handleShow} title="Edit or create album metadata">
          <EditIcon /> Edit
        </ModernButton>
      )}

      <StyledModal show={show} onHide={handleClose} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{isNew ? 'Create New Album' : 'Edit Album Metadata'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spinner animation="border" role="status" />
              <p style={{ marginTop: '10px' }}>Loading album data...</p>
            </div>
          ) : (
            <Form noValidate validated={validated} onSubmit={handleSubmit}>
              <SectionTitle>📝 Basic Information</SectionTitle>
              <FormRow>
                <Form.Group as={Col} md="6" controlId="validationTitle">
                  <Form.Label>Title</Form.Label>
                  <Form.Control
                    required
                    type="text"
                    placeholder="Enter album title"
                    value={album.title}
                    onChange={(e) => setAlbum({ ...album, title: e.target.value })}
                    size="sm"
                  />
                  <Form.Control.Feedback type="invalid">
                    Please provide a valid title
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group as={Col} md="3" controlId="validationDate">
                  <Form.Label>Date</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder="YYYY-MM-DD"
                    value={album.date}
                    onChange={(e) => setAlbum({ ...album, date: e.target.value })}
                  />
                </Form.Group>

                <Form.Group as={Col} md="3" controlId="validationRating">
                  <Form.Label>Rating</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max="10"
                    size="sm"
                    value={album.rating}
                    onChange={(e) => setAlbum({ ...album, rating: e.target.value })}
                  />
                </Form.Group>
              </FormRow>

              <FormRow>
                <Form.Group as={Col} md="12" controlId="validationDescription">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Enter album description"
                    value={album.description}
                    onChange={(e) => setAlbum({ ...album, description: e.target.value })}
                    size="sm"
                  />
                </Form.Group>
              </FormRow>

              <SectionTitle>🎭 Actors</SectionTitle>
              <ActorSearchContainer>
                <div className="search-input-wrapper">
                  <Select
                    style={{ width: "100%", fontSize: "12px" }}
                    multi
                    options={allActors}
                    placeholder="Add actor"
                    values={editActor.selectedActor}
                    labelField="name"
                    valueField="id"
                    color="#000000"
                    searchable={true}
                    sortBy="name"
                    clearable={true}
                    searchBy="name"
                    dropdownHeight="200px"
                    dropdownPosition="auto"
                    onChange={(e) => setEditActor({ ...editActor, selectedActor: e })}
                  />
                </div>
                <div className="button-group">
                  <ModernButton variant="light" onClick={addActor} size="sm" disabled={editActor.selectedActor.length === 0} title="Add selected actor">
                    <AddCircleOutlineIcon />
                  </ModernButton>
                </div>
              </ActorSearchContainer>

              {actors.selectedActor.length > 0 && (
                <Row className='m-0' style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(90px, 1fr))", gap: "12px", marginBottom: "16px" }}>
                  {actors.selectedActor.map(actor => (
                    <ActorCard key={`actor-${actor.id}`}>
                      <div 
                        className="actor-image"
                        style={{ backgroundImage: `url('${getActorImageUrl(actor.id)}')` }}
                      >
                        <div 
                          className="delete-badge"
                          onClick={() => removeActor(actor.id)}
                        >
                          <DeleteIcon />
                        </div>
                      </div>
                      <div className="actor-name">{actor.name}</div>
                    </ActorCard>
                  ))}
                </Row>
              )}

              <SectionTitle>🏷️ Tags & Categories</SectionTitle>
              <ActorSearchContainer>
                <div className="search-input-wrapper">
                  <Select
                    style={{ width: "100%", fontSize: "12px" }}
                    multi
                    options={allTags}
                    placeholder="Add tag"
                    values={editTag.selectedTag}
                    labelField="name"
                    valueField="id"
                    color="#000000"
                    searchable={true}
                    sortBy="name"
                    clearable={true}
                    searchBy="name"
                    dropdownHeight="200px"
                    dropdownPosition="auto"
                    onChange={(e) => setEditTag({ ...editTag, selectedTag: e })}
                  />
                </div>
                <div className="button-group">
                  <ModernButton variant="light" onClick={addTag} size="sm" disabled={editTag.selectedTag.length === 0} title="Add selected tag">
                    <AddCircleOutlineIcon />
                  </ModernButton>
                </div>
              </ActorSearchContainer>

              {tags.selectedTag.length > 0 && (
                <div style={{ marginBottom: "12px", display: "grid", gridTemplateColumns: "80px 1fr", gap: "12px", alignItems: "start" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    {Object.keys(getTagsByCategory()).map(category => (
                      <div key={`cat-${category}`} style={{ fontSize: "11px", fontWeight: "700", color: "#667eea", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        {category}
                      </div>
                    ))}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    {Object.entries(getTagsByCategory()).map(([category, categoryTags]) => (
                      <div key={`tags-${category}`} className="tags-wrapper" style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
                        {categoryTags.map(tag => (
                          <TagBadge 
                            key={`tag-${tag.id}`}
                            onClick={() => removeTag(tag.id)}
                            style={{ cursor: "pointer" }}
                          >
                            <span>{tag.name}</span>
                          </TagBadge>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {album.id && (
                <>
                  <SectionTitle>👗 Garments</SectionTitle>
                  
                  {/* Display tagged garments */}
                  {albumGarments.length > 0 && (
                    <div style={{ marginBottom: "16px" }}>
                      <div style={{ fontSize: "12px", fontWeight: "600", color: "#667eea", marginBottom: "8px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span>Tagged Garments ({albumGarments.length})</span>
                        <ModernButton 
                          variant="light" 
                          size="sm" 
                          onClick={() => fetchAlbumGarments(album.id)}
                          disabled={loadingGarments}
                          title="Refresh garment list"
                        >
                          {loadingGarments ? <Spinner animation="border" size="sm" /> : '🔄 Refresh'}
                        </ModernButton>
                      </div>
                      <Row style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: "8px" }}>
                        {albumGarments.map(garment => (
                          <GarmentCard key={`album-garment-${garment.id}`}>
                            {garment.image_url && (
                              <div 
                                className="garment-image"
                                style={{ backgroundImage: `url(${getGarmentImageUrl(garment.image_url)})` }}
                              />
                            )}
                            <div 
                              className="delete-badge"
                              onClick={() => removeGarmentFromAlbum(garment.id)}
                              title="Remove garment from album"
                            >
                              <DeleteIcon />
                            </div>
                            <div className="garment-info">
                              <div className="garment-name" title={garment.name}>{garment.name}</div>
                              {garment.category_name && (
                                <div className="garment-category">{garment.category_name}</div>
                              )}
                              {garment.actor_name && (
                                <div className="garment-actor">{garment.actor_name}</div>
                              )}
                            </div>
                          </GarmentCard>
                        ))}
                      </Row>
                    </div>
                  )}

                  {loadingGarments && (
                    <div style={{ textAlign: "center", padding: "16px", color: "#999" }}>
                      <Spinner animation="border" size="sm" style={{ marginRight: "6px" }} />
                      Loading garments...
                    </div>
                  )}

                  {!loadingGarments && albumGarments.length === 0 && (
                    <div style={{ padding: "12px", backgroundColor: "#f5f5f5", borderRadius: "6px", marginBottom: "16px", fontSize: "12px", color: "#999", textAlign: "center" }}>
                      No garments tagged to this album yet
                    </div>
                  )}

                  {/* Garment Manager for adding/removing garments */}
                  <div style={{ marginTop: "12px" }}>
                    <div style={{ fontSize: "12px", fontWeight: "600", color: "#667eea", marginBottom: "8px" }}>Manage Garments</div>
                    <GarmentManager 
                      albumId={album.id} 
                      onGarmentTagsUpdated={() => fetchAlbumGarments(album.id)}
                    />
                  </div>
                </>
              )}

              <SectionTitle>🎬 Linked Scene</SectionTitle>
              {linkedScene ? (
                <div style={{ padding: "12px", backgroundColor: "#f0f4ff", borderRadius: "6px", marginBottom: "16px", border: "1px solid #667eea" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "13px", fontWeight: "600", color: "#333", marginBottom: "4px" }}>
                        {linkedScene.title}
                      </div>
                      <div style={{ fontSize: "10px", color: "#999", marginBottom: "6px" }}>
                        Scene ID: {linkedScene.sceneId}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: "6px" }}>
                      <ModernButton variant="light" size="sm" onClick={() => setShowEditScene(true)} title="Edit scene information">
                        <EditIcon /> Edit
                      </ModernButton>
                      <ModernButton variant="light" size="sm" onClick={() => setLinkedScene(null)}>
                        <DeleteIcon /> Unlink
                      </ModernButton>
                    </div>
                  </div>
                  
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", fontSize: "11px", marginBottom: "8px" }}>
                    <div>
                      <div style={{ fontWeight: "600", color: "#667eea", marginBottom: "2px" }}>Date</div>
                      <div style={{ color: "#333" }}>
                        {linkedScene.date ? dayjs(linkedScene.date).format('YYYY-MM-DD') : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontWeight: "600", color: "#667eea", marginBottom: "2px" }}>Rating</div>
                      <div style={{ color: "#333" }}>
                        {linkedScene.rating || 'N/A'}
                      </div>
                    </div>
                  </div>

                  {linkedScene.description && (
                    <div style={{ fontSize: "11px", marginBottom: "8px" }}>
                      <div style={{ fontWeight: "600", color: "#667eea", marginBottom: "2px" }}>Description</div>
                      <div style={{ color: "#555", maxHeight: "60px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "pre-wrap" }}>
                        {linkedScene.description}
                      </div>
                    </div>
                  )}

                  {linkedScene.tags && linkedScene.tags.length > 0 && (
                    <div style={{ fontSize: "11px", marginBottom: "8px" }}>
                      <div style={{ fontWeight: "600", color: "#667eea", marginBottom: "4px" }}>Tags</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                        {linkedScene.tags.map(tag => (
                          <div
                            key={`tag-${tag.id}`}
                            style={{
                              display: "inline-block",
                              padding: "2px 6px",
                              borderRadius: "10px",
                              fontSize: "10px",
                              fontWeight: "600",
                              background: "linear-gradient(135deg, #fff4e6 0%, #ffe0b3 100%)",
                              color: "#333",
                              border: "1px solid #ffc107"
                            }}
                            title={tag.category}
                          >
                            {tag.name}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {linkedScene.garments && linkedScene.garments.length > 0 && (
                    <div style={{ fontSize: "11px", marginBottom: "8px" }}>
                      <div style={{ fontWeight: "600", color: "#667eea", marginBottom: "4px" }}>👗 Garments</div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))", gap: "8px" }}>
                        {linkedScene.garments.map(garment => (
                          <div
                            key={`garment-${garment.garment_id}`}
                            style={{
                              padding: "6px",
                              borderRadius: "6px",
                              backgroundColor: "#fff9e6",
                              border: "1px solid #ffe0b3",
                              textAlign: "center",
                              display: "flex",
                              flexDirection: "column",
                              gap: "4px"
                            }}
                          >
                            {garment.image_url && (
                              <div
                                style={{
                                  width: "100%",
                                  height: "80px",
                                  backgroundImage: `url('${getGarmentImageUrl(garment.image_url)}')`,
                                  backgroundSize: "cover",
                                  backgroundPosition: "center",
                                  borderRadius: "4px",
                                  backgroundColor: "#f0f0f0"
                                }}
                              />
                            )}
                            <div style={{ fontWeight: "600", color: "#333", fontSize: "10px" }}>
                              {garment.name}
                            </div>
                            <div style={{ fontSize: "9px", color: "#666" }}>
                              {garment.category_name || 'Uncategorized'}
                            </div>
                            {garment.color && (
                              <div style={{ fontSize: "9px", color: "#999" }}>
                                {garment.color}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div style={{ fontSize: "10px", color: "#999", paddingTop: "6px", borderTop: "1px solid #e0e0e0" }}>
                    Gallery Path: {linkedScene.gallery}
                  </div>
                </div>
              ) : (
                <div style={{ padding: "12px", backgroundColor: "#f5f5f5", borderRadius: "6px", marginBottom: "16px", border: "1px dashed #ccc", color: "#999", fontSize: "12px" }}>
                  No scene linked. Create a scene with gallery: "{galleryFolderName}/{album.folder_name}" to auto-link it.
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px', paddingTop: '12px', borderTop: '1px solid #e0e0e0' }}>
                <ModernButton variant="secondary" onClick={handleClose}>
                  <CancelIcon /> Cancel
                </ModernButton>
                <ModernButton variant="primary" onClick={handleSubmit} disabled={saving}>
                  {saving ? (
                    <>
                      <Spinner animation="border" size="sm" /> Saving...
                    </>
                  ) : (
                    <>
                      <SaveIcon /> {isNew ? 'Create Album' : 'Save Changes'}
                    </>
                  )}
                </ModernButton>
              </div>
            </Form>
          )}
        </Modal.Body>
      </StyledModal>

      {linkedScene && showEditScene && (
        <EditSceneInfo sceneId={linkedScene.id} onClose={() => setShowEditScene(false)} />
      )}
    </>
  );
}

export default EditAlbum;
